import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colorPalette } from '../../theme';

// Interface for individual options
interface SliderToggleOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
  'data-qa'?: string;
}

// Props for the SliderToggle component
export interface SliderToggleProps<T extends string> {
  options: SliderToggleOption<T>[];
  value: T; // Current active value
  onChange: (event: React.MouseEvent<HTMLElement>, newValue: T) => void;
  'data-qa'?: string; // data-qa for the group
  'aria-label'?: string; // Accessible label for the radiogroup
}

const SLIDER_CONTAINER_PADDING = 4; // Padding inside the container, thumb sits within this

// Main container for the slider
const StyledSliderContainer = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  position: 'relative',
  backgroundColor: colorPalette.redesign.background1,
  borderRadius: theme.shape.borderRadius * 2, // Less rounded shape
  padding: `${SLIDER_CONTAINER_PADDING}px`,
  border: `1px solid ${theme.palette.grey[300]}`,
  overflow: 'hidden', // Ensures thumb's rounded corners look good
  alignItems: 'center',
}));

// The sliding thumb element
const StyledSliderThumb = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: `${SLIDER_CONTAINER_PADDING}px`,
  bottom: `${SLIDER_CONTAINER_PADDING}px`,
  backgroundColor: colorPalette.redesign.lightPrimaryBlue,
  borderRadius: theme.shape.borderRadius * 1.5, // Less rounded, fits container
  transition: theme.transitions.create(['left', 'width'], {
    duration: theme.transitions.duration.short,
    easing: theme.transitions.easing.easeInOut,
  }),
  zIndex: 1, // Thumb is behind the text/icons
}));

// Props for individual option buttons
interface StyledSliderOptionButtonProps {
  isActive: boolean;
}

// Clickable option buttons (transparent, text changes color)
const StyledSliderOptionButton = styled(Box)<StyledSliderOptionButtonProps>(
  ({ theme, isActive }) => ({
    cursor: 'pointer',
    padding: theme.spacing(0.75, 2), // Adjust padding for text/icon spacing
    textAlign: 'center',
    flexGrow: 1, // Options share space equally
    flexShrink: 0,
    flexBasis: 0, // Ensures flex-grow works as expected from a zero base
    position: 'relative', // To ensure zIndex works
    zIndex: 2, // Text and icons are on top of the thumb
    color: isActive
      ? theme.palette.primary.contrastText
      : colorPalette.redesign.contentSecondary,
    transition: theme.transitions.create(['color'], {
      duration: theme.transitions.duration.short,
    }),
    userSelect: 'none', // Prevent text selection on click
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '28px', // Ensure a minimum height, adjust as needed

    '& .MuiTypography-root': {
      color: 'inherit', // Typography inherits color from this button
      fontWeight: 500,
      textTransform: 'none', // Prevent capitalization
    },
    '& > .slider-option-icon': {
      // Class for icon span
      marginRight: theme.spacing(0.5), // Space between icon and text
      display: 'flex',
      alignItems: 'center',
    },
  })
);

export function SliderToggle<T extends string>({
  options,
  value,
  onChange,
  'data-qa': dataQa,
  'aria-label': ariaLabel,
}: SliderToggleProps<T>) {
  const [thumbStyle, setThumbStyle] = useState<{ left: number; width: number }>(
    {
      left: 0,
      width: 0,
    }
  );

  // Refs for each option button and the container
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Effect to update thumb position when value or options change
  useEffect(() => {
    const activeOptionIndex = options.findIndex((opt) => opt.value === value);
    const activeOptionElement = optionRefs.current[activeOptionIndex];

    if (activeOptionElement && containerRef.current) {
      // Calculate position relative to the container
      const newLeft = activeOptionElement.offsetLeft;
      const newWidth = activeOptionElement.offsetWidth;

      setThumbStyle({
        left: newLeft,
        width: newWidth,
      });
    }
  }, [value, options]); // Rerun if value or options array changes

  const handleOptionClick = (
    event: React.MouseEvent<HTMLElement>,
    newValue: T
  ) => {
    onChange(event, newValue); // Call the passed onChange handler
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    newValue: T
  ) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      // Simulate a mouse event or pass a generic event object if needed by onChange
      // For simplicity, creating a minimal event-like structure or null
      const mockEvent = {} as React.MouseEvent<HTMLElement>;
      onChange(mockEvent, newValue);
    }
  };

  return (
    <StyledSliderContainer
      ref={containerRef}
      data-qa={dataQa}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      <StyledSliderThumb style={thumbStyle} />
      {options.map((option, index) => (
        <StyledSliderOptionButton
          key={option.value}
          ref={(el: HTMLDivElement | null) => (optionRefs.current[index] = el)} // Assign ref for this option
          isActive={option.value === value}
          onClick={(e: React.MouseEvent<HTMLElement>) =>
            handleOptionClick(e, option.value)
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLElement>) =>
            handleKeyDown(e, option.value)
          }
          data-qa={option['data-qa'] || `slider-toggle-button-${option.value}`}
          tabIndex={0}
          role="radio"
          aria-checked={option.value === value}
        >
          {option.icon && (
            <span className="slider-option-icon">{option.icon}</span>
          )}
          {option.label && (
            <Typography variant="button">{option.label}</Typography>
          )}
        </StyledSliderOptionButton>
      ))}
    </StyledSliderContainer>
  );
}

export default SliderToggle;
